import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stable client-side row id for React keys on user-editable lists
const uid = () => (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);

const newStandard = () => ({ _uid: uid(), code: '', description: '' });
const newDiscussionPoint = () => ({ _uid: uid(), value: '' });
const newTheme = () => ({ _uid: uid(), historical_context: '', present_day_connection: '', discussion_points: [newDiscussionPoint()] });

export default function CreateUnit() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialGradeLevel = location.state?.gradeLevel || 9;

  const [formData, setFormData] = useState({
    grade_level: initialGradeLevel,
    unit_number: 1,
    title: '',
    description: '',
    teacher_notes: ''
  });

  const [standards, setStandards] = useState([newStandard()]);
  const [themes, setThemes] = useState([newTheme()]);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        kansas_standards: standards
          .filter(s => s.code && s.description)
          .map(({ _uid: _u, ...rest }) => rest),
        themes_to_present: themes
          .filter(t => t.historical_context && t.present_day_connection)
          .map(({ _uid: _u, discussion_points, ...rest }) => ({
            ...rest,
            discussion_points: (discussion_points || []).map(d => d.value).filter(Boolean),
          })),
      };

      const response = await axios.post(`${API}/units`, payload);
      toast.success('Unit created successfully!');
      navigate(`/unit/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create unit');
    } finally {
      setSaving(false);
    }
  };

  const addStandard = () => {
    setStandards([...standards, newStandard()]);
  };

  const removeStandard = (index) => {
    setStandards(standards.filter((_, i) => i !== index));
  };

  const updateStandard = (index, field, value) => {
    const updated = [...standards];
    updated[index][field] = value;
    setStandards(updated);
  };

  const addTheme = () => {
    setThemes([...themes, newTheme()]);
  };

  const removeTheme = (index) => {
    setThemes(themes.filter((_, i) => i !== index));
  };

  const updateTheme = (index, field, value) => {
    const updated = [...themes];
    updated[index][field] = value;
    setThemes(updated);
  };

  const addDiscussionPoint = (themeIndex) => {
    const updated = [...themes];
    updated[themeIndex].discussion_points.push(newDiscussionPoint());
    setThemes(updated);
  };

  const updateDiscussionPoint = (themeIndex, pointIndex, value) => {
    const updated = [...themes];
    updated[themeIndex].discussion_points[pointIndex].value = value;
    setThemes(updated);
  };

  const removeDiscussionPoint = (themeIndex, pointIndex) => {
    const updated = [...themes];
    updated[themeIndex].discussion_points = updated[themeIndex].discussion_points.filter((_, i) => i !== pointIndex);
    setThemes(updated);
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/dashboard/${formData.grade_level}`)}
          className="mb-4 text-[#5A6577] hover:text-[#14213D]"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Admin</p>
          <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight mb-2">
            Create New Unit
          </h1>
          <p className="text-sm text-[#5A6577]">Design a new curriculum unit aligned with Kansas HGSS standards.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <Card className="p-8">
            <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-6">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="grade_level">Grade Level *</Label>
                <Select
                  value={formData.grade_level.toString()}
                  onValueChange={(value) => setFormData({ ...formData, grade_level: parseInt(value) })}
                >
                  <SelectTrigger id="grade_level" data-testid="grade-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="9">Grade 9 - World History</SelectItem>
                    <SelectItem value="10">Grade 10 - Modern World History</SelectItem>
                    <SelectItem value="11">Grade 11 - US History</SelectItem>
                    <SelectItem value="12">Grade 12 - Government & Economics</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="unit_number">Unit Number *</Label>
                <Input
                  id="unit_number"
                  type="number"
                  min="1"
                  max="12"
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: parseInt(e.target.value) })}
                  data-testid="unit-number-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Unit Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., The American Revolution"
                  data-testid="unit-title-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Provide a comprehensive overview of the unit..."
                  rows={4}
                  data-testid="unit-description-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="teacher_notes">Teacher Notes</Label>
                <Textarea
                  id="teacher_notes"
                  value={formData.teacher_notes}
                  onChange={(e) => setFormData({ ...formData, teacher_notes: e.target.value })}
                  placeholder="Add any additional notes or tips for teachers..."
                  rows={3}
                  data-testid="teacher-notes-input"
                />
              </div>
            </div>
          </Card>

          {/* Kansas Standards */}
          <Card className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif font-normal text-[#14213D]">Kansas HGSS Standards</h2>
              <Button type="button" variant="outline" size="sm" onClick={addStandard} data-testid="add-standard-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Standard
              </Button>
            </div>

            <div className="space-y-4">
              {standards.map((standard, index) => (
                <div key={standard._uid} className="flex gap-4 items-start border border-[#EBE7E0] rounded-lg p-4">
                  <div className="flex-1 space-y-3">
                    <Input
                      placeholder="Standard Code (e.g., HGSS 1.2)"
                      value={standard.code}
                      onChange={(e) => updateStandard(index, 'code', e.target.value)}
                      data-testid={`standard-code-${index}`}
                    />
                    <Textarea
                      placeholder="Standard Description"
                      value={standard.description}
                      onChange={(e) => updateStandard(index, 'description', e.target.value)}
                      rows={2}
                      data-testid={`standard-description-${index}`}
                    />
                  </div>
                  {standards.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStandard(index)}
                      data-testid={`remove-standard-${index}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Themes to Present */}
          <Card className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif font-normal text-[#14213D]">Connections to Present</h2>
              <Button type="button" variant="outline" size="sm" onClick={addTheme} data-testid="add-theme-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Connection
              </Button>
            </div>

            <div className="space-y-6">
              {themes.map((theme, themeIndex) => (
                <div key={theme._uid} className="border border-[#EBE7E0] rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-[#14213D]">Connection {themeIndex + 1}</h3>
                    {themes.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTheme(themeIndex)}
                        data-testid={`remove-theme-${themeIndex}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <Textarea
                    placeholder="Historical Context"
                    value={theme.historical_context}
                    onChange={(e) => updateTheme(themeIndex, 'historical_context', e.target.value)}
                    rows={2}
                    data-testid={`theme-historical-${themeIndex}`}
                  />

                  <Textarea
                    placeholder="Present-Day Connection"
                    value={theme.present_day_connection}
                    onChange={(e) => updateTheme(themeIndex, 'present_day_connection', e.target.value)}
                    rows={2}
                    data-testid={`theme-present-${themeIndex}`}
                  />

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm">Discussion Points</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => addDiscussionPoint(themeIndex)}
                        data-testid={`add-discussion-point-${themeIndex}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Point
                      </Button>
                    </div>
                    {theme.discussion_points.map((point, pointIndex) => (
                      <div key={point._uid} className="flex gap-2 mb-2">
                        <Input
                          placeholder="Discussion point"
                          value={point.value}
                          onChange={(e) => updateDiscussionPoint(themeIndex, pointIndex, e.target.value)}
                          data-testid={`discussion-point-${themeIndex}-${pointIndex}`}
                        />
                        {theme.discussion_points.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDiscussionPoint(themeIndex, pointIndex)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/dashboard/${formData.grade_level}`)}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="create-unit-submit-btn">
              {saving ? 'Creating...' : 'Create Unit'}
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
